import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import classnames from "classnames";
import styles from "./CouponTable.scss";
import * as CouponsApi from "api/CouponsApi";
import ApprovalStatusIcon from "components/common/ApprovalStatusIcon";
import CouponOptionMenu from "components/coupons/widgets/CouponOptionMenu";
import OpenInvoicePDFLinkNewTab from "components/common/OpenInvoicePDFLinkNewTab";
import SortableDataTable from "components/common/SortableDataTable";
import logger from "utils/logger";
import {downloadFile} from "utils/request";
import toast from "utils/toast";
import * as StatusId from "constants/CouponStatusIds";
import {APPROVED} from "constants/CouponStatusIds";

export default function CouponTable(props) {
    const COLUMNS = [
        {
            name: "Inv. #",
            selector: "invoiceNumber",
            key: "invoiceNumber",
            sortable: true,
            format: row => (
                <OpenInvoicePDFLinkNewTab
                    className="text-primary"
                    invoiceId={row.invoiceId}
                    extendedInvoiceNumber={row.extendedInvoiceNumber}
                    groupOfferId={row.isGroupOffer ? row.actualGroupRedemptionId : null}
                />
            ),
        },
        {
            name: "Inv. Date",
            selector: "invoiceDate",
            key: "invoiceDate",
            sortable: true,
            format: row => moment(row.invoiceDate).format('MM/DD/YYYY'),
        },
        {
            name: "Last Name",
            selector: "lastName",
            key: "lastName",
            sortable: true,
        },
        {
            name: "Pet Name",
            selector: "petName",
            key: "petName",
            sortable: true,
        },
        {
            name: "Product",
            selector: "product",
            key: "product",
            sortable: true,
            format: row => (<div><span>{row.product} / {row.quantity}</span></div>),
        },
        {
            name: "Offer",
            selector: "offerName",
            key: "offerName",
            sortable: true,
        },
        {
            name: props.showStatusIcon ? "Status" : "",
            selector: "status",
            key: "status",
            // sortable: props.showStatusIcon,
            format: row => getStatusAndMenu(row, props),
        },
    ];

    const handleDownloadExcel = async () => {
        if(props.canViewClinicCouponAudit) {
            toast.success("Generating Export...");

            const res = await CouponsApi.downloadCouponHistory(props.downloadOptions);
            downloadFile(res, "xlsx");
        }
    }

    const canSelect = props.canSelect && props.canEditClinicCouponAudit && (!props.historical || props.canUndoDeclined);

    return (
        <SortableDataTable
            columns={COLUMNS}
            rawData={props.coupons}
            striped
            selectable={canSelect}
            selectedRowIds={props.selectedRowIds}
            onRowSelectChange={props.onRowSelectChange}
            noPagination={!props.resultsPerPage}
            resultsPerPage={props.resultsPerPage}
            selectionDisabledIcon={(row) => {
                if (row.status === APPROVED) {
                    // Returns nothing if the status of the row is "Approved"
                    return null;
                } else {
                    // Returns a clickable warning icon if the row has errors
                    return(
                        <span
                            className={classnames("margin-right-sm fa-lg", {
                                "pointer": props.canEditClinicCouponAudit,
                            })}
                            onClick={() => props.canEditClinicCouponAudit ? props.onEditClick(row.id) : {}}
                        >
                            <i className="fas fa-exclamation-triangle text-danger"/>
                        </span>
                    );
                }
            }}
            downloadExcel={!!(props.historical && props.canViewClinicCouponAudit) ? handleDownloadExcel : null}
            // onSort={props.handleSort}
        />
    );
}

function getStatusAndMenu({status, invoiceNumber, id, requiresValidation}, props) {
    return (
        <div className={styles.status}>
            {props.showStatusIcon && (
                <ApprovalStatusIcon
                    status={status}
                />
            )}
            {props.canEditClinicCouponAudit && props.showOptionsMenu && (
                <CouponOptionMenu
                    onEditClick={() => (props.canEditClinicCouponAudit && props.onEditClick) ? props.onEditClick(id) : logger.log("no handler")}
                    // onPdfClick={() => props.onPdfClick ? props.onPdfClick(invoiceNumber) : logger.log("no handler")}
                    // onShareClick={() => props.onShareClick ? props.onShareClick(id) : {}}
                    onApproveClick={() => props.onApproveClick ? props.onApproveClick(id) : {}}
                    onDeclineClick={() => props.onDeclineClick ? props.onDeclineClick(id) : {}}
                    canEdit={requiresValidation}
                    canApprove={!(requiresValidation || status === StatusId.APPROVED)}
                    canDecline={props.onDeclineClick && status !== StatusId.DECLINED}
                />
            )}
        </div>
    )
}

CouponTable.propTypes = {
    coupons: PropTypes.arrayOf(PropTypes.object).isRequired,
    historical: PropTypes.bool,
    resultsPerPage: PropTypes.number,
    downloadOptions: PropTypes.shape({
        period: PropTypes.string.isRequired,
        clinicId: PropTypes.string.isRequired,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),

    // Row display options
    showOptionsMenu: PropTypes.bool,
    showStatusIcon: PropTypes.bool,
    showVendorLogos: PropTypes.bool,

    // Row menu click handlers
    onEditClick: PropTypes.func,
    // onPdfClick: PropTypes.func,
    onShareClick: PropTypes.func,
    onApproveClick: PropTypes.func,
    onDeclineClick: PropTypes.func,

    onInvoiceClick: PropTypes.func,
    onRowSelectChange: PropTypes.func,
    // handleSort: PropTypes.func,
    selectedRowIds: PropTypes.array,

    canSelect: PropTypes.bool, // Disable row selection checkboxes entirely
    canViewClinicCouponAudit: PropTypes.bool,
    canEditClinicCouponAudit: PropTypes.bool,
};

CouponTable.defaultProps = {
    showOptionsMenu: true,
    showStatusIcon: true,
    canSelect: true,
};
